import { gql } from 'apollo-angular';

export const InstitutionForCalculatorFragmentFragmentDoc = gql`
  fragment institutionForCalculatorFragment on Institution {
    unitId
    name
    city
    url
    stateAbbr
    zipCode
    retentionRateFullTime
    admissionTestScores {
      sat {
        math {
          percentileScore25
          percentileScore75
        }
        readingAndWriting {
          percentileScore25
          percentileScore75
        }
        composite {
          percentileScore25
          percentileScore75
        }
      }
      act {
        math {
          percentileScore25
          percentileScore75
        }
        english {
          percentileScore25
          percentileScore75
        }
        composite {
          percentileScore25
          percentileScore75
        }
      }
    }
    levelTypeName
    admissionRate
    controlTypeName
    costOfAttendanceInfo {
      tuitionAndFees {
        inDistrict {
          fullTimeExpense
          partTimeExpense
          percentChangeFromLastYear
        }
        inState {
          fullTimeExpense
          partTimeExpense
          percentChangeFromLastYear
        }
        outOfState {
          fullTimeExpense
          partTimeExpense
          percentChangeFromLastYear
        }
      }
      booksAndSupplies {
        expenseAmount
        percentChangeFromLastYear
      }
      livingArrangement {
        onCampus {
          roomAndBoard {
            expenseAmount
            percentChangeFromLastYear
          }
          otherExpenses {
            expenseAmount
            percentChangeFromLastYear
          }
        }
        offCampusNotWithFamily {
          roomAndBoard {
            expenseAmount
            percentChangeFromLastYear
          }
          otherExpenses {
            expenseAmount
            percentChangeFromLastYear
          }
        }
        offCampusWithFamily {
          otherExpenses {
            expenseAmount
            percentChangeFromLastYear
          }
        }
      }
    }
    avgGrantScholarshipAidBreakdownInfo {
      federalGrants {
        pellGrants {
          avgAmountAidReceived
        }
        otherFederalGrants {
          avgAmountAidReceived
        }
      }
      stateLocalGovtGrantOrScholarships {
        avgAmountAidReceived
      }
      institutionalGrantsOrScholarships {
        avgAmountAidReceived
      }
    }
    avgGrantScholarshipAidByIncomeInfo {
      income0To30000
      income30001To48000
      income48001To75000
      income75001To110000
      incomeOver110000
    }
    studentBody {
      studentBodySize
      totalCount
      fullTimePercent
      partTimePercent
    }
    fullTimeGraduationRates {
      gr100
      gr150Default
      gr200
    }
    partTimeGraduationRates {
      gr100
      gr150
      gr200
    }
    undergraduateApplicationFee
    genderList {
      name
      type
      percent
    }
  }
`;

export const CipSalaryAnalysisByZipCodeDocument = gql`
  query cipSalaryAnalysisByZipCode($cipCode: String!, $zipCode: String!) {
    cipSalaryAnalysisByZipCode(cipCode: $cipCode, zipCode: $zipCode) {
      salarySocCode
      salaryPrecisionType
      minimumAnnualSalary
      medianAnnualSalary
      maximumAnnualSalary
      geographyName
    }
  }
`;

export const LocationFragmentFragmentDoc = gql`
  fragment locationFragment on Location {
    zipCode
    marketAreaCityName
    cityName
    countyFIPSCode
    countyName
    stateAbbreviation
    stateName
    medianAnnualIndividualSalary
    costOfLivingList {
      expenseCategoryGroupId
      expenseCategoryGroupName
      expenseCategoryId
      expenseCategoryName
      averageAnnualExpense
      averageMonthlyExpense
    }
    latitude
    longitude
    autoCompleteModel
  }
`;

export const ExchangeAutoCompleteForLocationDocument = gql`
  query exchangeAutoCompleteForLocation($autoCompleteModel: AutoCompleteModelInput!, $zipCode: ID, $distance: Int) {
    exchangeAutoCompleteForLocation(autoCompleteModel: $autoCompleteModel, zipCode: $zipCode, distance: $distance) {
      nearbyCountyFipsCodes(zipCode: $zipCode, distance: $distance)
      ...locationFragment
    }
  }
  ${LocationFragmentFragmentDoc}
`;

export const InstitutionByUnitIdDocument = gql`
  query institutionByUnitId($unitId: ID!, $cipCode: String, $paging: OffsetPaging) {
    institution(id: $unitId) {
      ...institutionForCalculatorFragment
      educationLevelTypeIds
      institutionAllPrograms {
        instructionalProgram {
          cipCode
          cipTitle
          ipedsEducationLevel(unitId: $unitId)
        }
      }
      institutionPrograms(filter: { cipCode: { eq: $cipCode } }, paging: $paging) {
        nodes {
          cipCode
          instructionalProgram {
            cipCode
            cipTitle
            ipedsEducationLevel
          }
          csEducationLevelAlumniEarningsList {
            type
            name
            alumniEarningsAfter1Year
            alumniEarningsAfter2Years
          }
        }
      }
    }
  }
  ${InstitutionForCalculatorFragmentFragmentDoc}
`;

export const OptimizedLoansMappingDocument = gql`
  query optimizedLoansMapping(
    $netPriceByYear: [Float!]!
    $numYears: Int!
    $isTaxIndependent: Boolean!
    $federalLoanInterest: Float!
    $federalLoanYears: Int!
    $privateLoanInterest: Float!
    $privateLoanYears: Int!
    $isEligibleForSubsidizedLoanByYear: [Boolean]
  ) {
    optimizedLoansMapping(
      netPriceByYear: $netPriceByYear
      numYears: $numYears
      isTaxIndependent: $isTaxIndependent
      federalLoanInterest: $federalLoanInterest
      federalLoanYears: $federalLoanYears
      privateLoanInterest: $privateLoanInterest
      privateLoanYears: $privateLoanYears
      isEligibleForSubsidizedLoanByYear: $isEligibleForSubsidizedLoanByYear
    ) {
      inputHash
      x
      y
    }
  }
`;

export const AutocompleteInstitutions = gql`
  query autocompleteInstitutions($keyword: String!) {
    autocompleteInstitutions(keyword: $keyword) {
      id
      name
    }
  }
`;

export const UserReport = gql`
  mutation CalculateResult($input: OccupAICalculatorInputDTO!) {
    calculateResult(input: $input, userprofileId: "123") {
      data {
        breakEvenPointInYears
        reportFigures {
          baselineAnnualIncome
          annualIncomeChange
          monthlyIncomeChange
          incomeChangePercent
          workingYearsAfterGraduation
          tenYearWindowYears
          lifetimeEarningsBoost
          incomeGivenUpWhileStudying
          reasonCodes
        }
        targetEducationAndCareer {
          institution
          degreeLevel
          targetCipCode
          targetCity
          targetState
        }
        userProfile {
          age
          retirementAge
          highestEducationLevelAttained
          completedCipCode
          currentJobTitle
          currentCity
          currentState
          currentMonthlyIncome
          residencyStatus
          enrollmentType
          housingPlan
          estimatedFamilyIncome
        }
        studentFinancingInputs {
          startYear
          yearsUntilGraduation
          costOfCollegeAfterGrants
          fundingSource {
            militaryBenefits
            outOfPocketFunds
            otherFinancialSupport
            aidGrowthRate
          }
        }
        financingResults {
          results {
            year
            cost
            financialAid
            netPrice
            outOfPocket
            need
          }
          summary {
            totalCost
            monthlyPaymentGrand
            totalLoanPrincipalWithInterest
          }
        }
        currentIncome {
          avgMonthlyIncomeMedian
          incomeSourceType
          city
          state
          geographyScope
          radiusMiles
        }
        potentialIncome {
          city
          state
          geographyScope
          radiusMiles
          avgAnnualSalaryMedian
          avgMonthlyIncomeMedian
          avgAnnualSalaryPct10
          avgAnnualSalaryPct25
          avgAnnualSalaryPct75
          avgAnnualSalaryPct90
          avgMonthlyIncomePct10
          avgMonthlyIncomePct25
          avgMonthlyIncomePct75
          avgMonthlyIncomePct90
        }
        returnOnInvestment {
          tenYearRoi {
            roi
            roiPercent
            netGain
          }
          lifetimeRoi {
            roi
            roiPercent
            netGain
          }
        }
        overallRecommendation
        analysis {
          incomeProjection
          returnOnInvestment
          costVSBenefit
        }
      }
    }
  }
`;

export const GenerateAdvice = gql`
  query GenerateAdvice($questionId: Int!, $input: QueryParamsInputDTO!) {
    generateAdvice(questionId: $questionId, params: $input) {
      interpretation
      conclusion
      recommendations
    }
  }
`;
